import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
} from "@fluentui/react-components";
import { MoreHorizontal24Regular } from "@fluentui/react-icons";

import { Document, Page, pdfjs } from "react-pdf";

// Important configuration step:
// react-pdf uses Mozilla PDF.js under the hood. PDF.js runs parsing in a Web Worker
// so the UI thread stays responsive while rendering.
//
// Security note:
// Previously we loaded the worker from a third-party CDN (unpkg.com), which is a
// supply-chain risk and also fights CSP enforcement.
//
// With webpack 5, we can bundle the worker from our own dependencies and serve it
// from the same origin as the add-in.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

// We define the "props" that our component will accept.
// In this case, it just needs a URL for the PDF file.
interface PdfViewerProps {
  fileUrl?: string; // optional: direct URL
  fileData?: ArrayBuffer; // optional: raw bytes (preferred for Graph proxy raw)
  page?: number;
  onClose?: () => void; // optional: allow parent to exit viewer
}

// This is our actual React component. It's a function that takes our props
// and returns the JSX to be rendered.
const PdfViewer: React.FC<PdfViewerProps> = ({ fileUrl, fileData, page: pageProp, onClose }) => {
  const fileProp = fileData ? { data: fileData } : fileUrl;

  const [numPages, setNumPages] = useState<number | undefined>(undefined);
  const [page, setPage] = useState<number>(pageProp ?? 1);
  const [scale, setScale] = useState<number>(1);
  const [zoomDraft, setZoomDraft] = useState<string>("100");
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [isCompactToolbar, setIsCompactToolbar] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const MIN_SCALE = 0.5;
  const MAX_SCALE = 2;
  const STEP = 0.1;

  const clampValue = useCallback(
    (value: number, min: number, max: number) => Math.min(max, Math.max(min, value)),
    []
  );

  // Sync internal page when parent updates it
  useEffect(() => {
    if (typeof pageProp === "number" && pageProp >= 1 && (!numPages || pageProp <= numPages)) {
      setPage(pageProp);
    }
  }, [pageProp, numPages]);

  const handleLoadSuccess = useCallback(
    (pdf: any) => {
      const total = pdf?.numPages as number;
      setNumPages(total);
      // Clamp to pageProp if provided, else clamp current page
      setPage((prev) => {
        const desired = typeof pageProp === "number" ? pageProp : prev || 1;
        if (!total) return desired;
        return Math.min(Math.max(desired, 1), total);
      });
    },
    [pageProp]
  );

  useEffect(() => {
    setZoomDraft(String(Math.round(scale * 100)));
  }, [scale]);

  // Observe container width so the PDF scales responsively.
  // React-pdf triggers layout thrashes inside Excel's WebView, so we debounce updates
  // to avoid the "ResizeObserver loop completed with undelivered notifications" error.
  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    let queuedWidth: number | null = null;
    let rafId: number | null = null;

    const flushWidth = () => {
      if (queuedWidth === null) {
        return;
      }
      const next = queuedWidth;
      queuedWidth = null;
      setContainerWidth((prev) => (prev === next ? prev : next));
      rafId = null;
    };

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      queuedWidth = entry.contentRect?.width ?? node.clientWidth;
      if (rafId !== null) {
        return;
      }
      rafId = window.requestAnimationFrame(flushWidth);
    });

    observer.observe(node);

    return () => {
      observer.disconnect();
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, []);

  useEffect(() => {
    const COLLAPSE_THRESHOLD = 620;
    const EXPAND_THRESHOLD = 670;

    if (containerWidth === null) {
      if (isCompactToolbar) {
        setIsCompactToolbar(false);
      }
      return;
    }

    if (isCompactToolbar) {
      if (containerWidth > EXPAND_THRESHOLD) {
        setIsCompactToolbar(false);
      }
    } else if (containerWidth < COLLAPSE_THRESHOLD) {
      setIsCompactToolbar(true);
    }
  }, [containerWidth, isCompactToolbar]);

  // Keyboard navigation: Left/Right arrows move between pages
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!numPages) return;
      if (e.key === "ArrowRight") {
        setPage((p) => Math.min((p ?? 1) + 1, numPages));
      } else if (e.key === "ArrowLeft") {
        setPage((p) => Math.max((p ?? 1) - 1, 1));
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [numPages]);

  // Controlled input for page number (simple, forgiving parsing)
  const handlePageInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.trim();
    const n = Number(raw);
    if (!Number.isFinite(n)) return; // ignore non-numeric
    if (!numPages) return; // ignore until doc loaded
    const clamped = Math.min(Math.max(Math.floor(n), 1), numPages);
    setPage(clamped);
  };

  const commitZoomDraft = useCallback(() => {
    const numeric = Number(zoomDraft);
    if (!Number.isFinite(numeric)) {
      setZoomDraft(String(Math.round(scale * 100)));
      return;
    }
    const normalized = clampValue(Math.round(numeric), MIN_SCALE * 100, MAX_SCALE * 100);
    setScale(normalized / 100);
    setZoomDraft(String(normalized));
  }, [zoomDraft, scale, clampValue]);

  const handleZoomDelta = (delta: number) => {
    setScale((prev) => {
      const next = clampValue(Number((prev + delta).toFixed(2)), MIN_SCALE, MAX_SCALE);
      return next;
    });
  };

  const handleZoomPercentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setZoomDraft(e.target.value);
  };

  const handleZoomInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      commitZoomDraft();
    }
  };

  const resetZoom = () => setScale(1);

  const effectiveWidth = containerWidth ? Math.floor(containerWidth * scale) : undefined;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        background: "#edf6fe",
        height: "88vh",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {/* Toolbar: clearly separated from the PDF with border and background */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 12px",
          background: "#f5f5f5",
          borderBottom: "1px solid #ddd",
          flexWrap: "nowrap",
          overflowX: "auto",
        }}
      >
        <Button
          size="small"
          aria-label="Previous page"
          onClick={() => setPage((p) => Math.max((p ?? 1) - 1, 1))}
          disabled={!numPages || page <= 1}
        >
          ←
        </Button>

        <span style={{ fontSize: 12, whiteSpace: "nowrap" }}>Page</span>
        <Input
          aria-label="Page number"
          type="number"
          value={String(page)}
          onChange={handlePageInput}
          disabled={!numPages}
          appearance="outline"
          size="small"
          style={{ width: 72 }}
        />
        <span style={{ fontSize: 12, whiteSpace: "nowrap" }}>
          {numPages ? `of ${numPages}` : ""}
        </span>

        <Button
          size="small"
          aria-label="Next page"
          onClick={() => setPage((p) => Math.min((p ?? 1) + 1, numPages ?? 1))}
          disabled={!numPages || (numPages ? page >= numPages : true)}
        >
          →
        </Button>

        {!isCompactToolbar ? (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 16 }}>
            <Button
              size="small"
              aria-label="Zoom out"
              onClick={() => handleZoomDelta(-STEP)}
              disabled={scale <= MIN_SCALE}
            >
              −
            </Button>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Input
                aria-label="Zoom percentage"
                type="number"
                value={zoomDraft}
                onChange={handleZoomPercentChange}
                onBlur={commitZoomDraft}
                onKeyDown={handleZoomInputKey}
                appearance="outline"
                size="small"
                style={{ width: 56 }}
              />
              <span style={{ fontSize: 12 }}>%</span>
            </div>
            <Button
              size="small"
              aria-label="Zoom in"
              onClick={() => handleZoomDelta(STEP)}
              disabled={scale >= MAX_SCALE}
            >
              +
            </Button>
            <Button size="small" appearance="secondary" onClick={resetZoom}>
              Fit width
            </Button>
          </div>
        ) : (
          <Menu>
            <MenuTrigger>
              <MenuButton
                size="small"
                appearance="subtle"
                icon={<MoreHorizontal24Regular />}
                aria-label="Viewer options"
                style={{ marginLeft: 8 }}
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={() => handleZoomDelta(-STEP)} disabled={scale <= MIN_SCALE}>
                  Zoom out
                </MenuItem>
                <MenuItem onClick={() => handleZoomDelta(STEP)} disabled={scale >= MAX_SCALE}>
                  Zoom in
                </MenuItem>
                <MenuItem onClick={resetZoom}>Fit width</MenuItem>
                <MenuItem onClick={() => setScale(1)}>Default zoom (100%)</MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        )}

        <div style={{ flex: 1 }} />

        <Button size="small" appearance="secondary" onClick={onClose} disabled={!onClose}>
          Close
        </Button>
      </div>

      {/* Document region gains subtle separators so the PDF stands out from surrounding UI. */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          borderTop: "1px solid #d1d1d1",
          borderBottom: "1px solid #d1d1d1",
          padding: "16px 12px",
          display: "flex",
          justifyContent: "center",
          background: "#edf6fe",
          overflow: "auto",
          scrollbarGutter: "stable both-edges",
        }}
      >
        {/* The Document component renders the PDF without overlays so it matches Preview.app visuals. */}
        <Document
          file={fileProp}
          loading="Loading PDF..."
          error="Failed to load PDF file."
          onLoadSuccess={handleLoadSuccess}
        >
          {/* renderTextLayer/renderAnnotationLayer are disabled to hide transparent overlays that showed selectable text. */}
          <Page
            pageNumber={page}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            width={effectiveWidth}
          />
        </Document>
      </div>

      {/* Bottom spacer maintains brand background and provides visual balance under the PDF content. */}
      <div
        style={{
          height: 30,
          background: "#edf6fe",
          borderTop: "1px solid #d1d1d1",
          flexShrink: 0,
        }}
      />
    </div>
  );
};

// Finally, we export our component so other parts of the application can use it.
export default PdfViewer;
